import { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { send } from '../../../../api/modules/apiV2';
import { gamingScoreCoordinator } from '../../../../coordinators/init';
import { GamingScoreTimeoutError, shouldThrottleGameScoreError } from '../../../../coordinators/gamingScore/errors';

interface TierInfo {
  tierId: string;
  name: string;
  canEarn: boolean;
  priority?: number;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  detailSummary?: string;
  componentChecks?: ComponentCheck[];
}

export interface ComponentCheck {
  component: string;
  label: string;
  currentValue: string;
  score: number;
  requiredScore: number;
  passes: boolean;
}

interface NextTierInfo {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  componentChecks: ComponentCheck[];
}

export interface PreviousTierInfo {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
}

interface UseTierEvaluationResult {
  tier: TierInfo | null;
  nextTier: NextTierInfo | null;
  previousTier: PreviousTierInfo | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
  initialFetchDone: boolean;
  initialFetchFailed: boolean;
}

interface TierDefinition {
  tierId: string;
  name: string;
  priority: number;
  canEarn: boolean;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  detailSummary?: string;
  nextTierEarnRateDescription?: string;
  nextTierMaxEarningsDescription?: string;
  requirements?: Record<string, { min?: number }>;
}

const COMPONENT_LABELS: Record<string, string> = {
  cpu: 'CPU',
  gpu: 'GPU',
  memory: 'Memory',
  storage: 'Storage',
  network: 'Network',
  videoEncoding: 'Video Encoding',
  environment: 'Environment',
  trust: 'Trust',
  osVersion: 'OS Version',
};

/**
 * On local dev, PlayJector never sends metrics so machineTierDetail is never
 * populated. This fetches tier definitions directly and builds synthetic
 * display data so the Welcome / About Tiers pages are testable.
 */
// TESTING: Set to true to simulate being on the max (last) tier locally
const LOCAL_TEST_AS_MAX_TIER = false;
// TESTING: Set to true to skip the local fallback and see the full loading screen
const LOCAL_SKIP_FALLBACK = true;

function parseTierDefinitions(tiers: TierDefinition[]): {
  tier: TierInfo;
  nextTier: NextTierInfo | null;
  previousTier: PreviousTierInfo | null;
} {
  const sorted = [...tiers].sort((a, b) => a.priority - b.priority);

  if (LOCAL_TEST_AS_MAX_TIER && sorted.length > 1) {
    const last = sorted[sorted.length - 1];
    const prev = sorted[sorted.length - 2];

    const checks: ComponentCheck[] = [];
    if (last.requirements) {
      for (const [key, req] of Object.entries(last.requirements)) {
        if (req?.min && req.min > 0) {
          checks.push({
            component: key,
            label: COMPONENT_LABELS[key] ?? key,
            currentValue: 'N/A (local)',
            score: req.min,
            requiredScore: req.min,
            passes: true,
          });
        }
      }
    }

    const tier: TierInfo = {
      tierId: last.tierId,
      name: last.name,
      canEarn: last.canEarn,
      priority: last.priority,
      earnRateDescription: last.earnRateDescription,
      maxEarningsDescription: last.maxEarningsDescription,
      detailSummary: last.detailSummary,
      componentChecks: checks,
    };

    const previousTier: PreviousTierInfo = {
      tierId: prev.tierId,
      name: prev.name,
      earnRateDescription: prev.earnRateDescription,
      maxEarningsDescription: prev.maxEarningsDescription,
    };

    return { tier, nextTier: null, previousTier };
  }

  const first = sorted[0];
  const second = sorted.length > 1 ? sorted[1] : null;

  const tier: TierInfo = {
    tierId: first?.tierId ?? 'tier-1',
    name: first?.name ?? 'Tier 1',
    canEarn: first?.canEarn ?? true,
    priority: first?.priority ?? 0,
    earnRateDescription: first?.earnRateDescription,
    maxEarningsDescription: first?.maxEarningsDescription,
    detailSummary: first?.detailSummary,
  };

  let nextTier: NextTierInfo | null = null;
  if (second) {
    const checks: ComponentCheck[] = [];
    if (second.requirements) {
      for (const [key, req] of Object.entries(second.requirements)) {
        if (req?.min && req.min > 0) {
          checks.push({
            component: key,
            label: COMPONENT_LABELS[key] ?? key,
            currentValue: 'N/A (local)',
            score: 0,
            requiredScore: req.min,
            passes: false,
          });
        }
      }
    }
    nextTier = {
      tierId: second.tierId,
      name: second.name,
      earnRateDescription: second.nextTierEarnRateDescription ?? second.earnRateDescription,
      maxEarningsDescription: second.nextTierMaxEarningsDescription ?? second.maxEarningsDescription,
      componentChecks: checks,
    };
  }

  return { tier, nextTier, previousTier: null };
}

const TIER_TIMEOUT_MS = 150_000;

const LOCAL_RETRY_DELAYS_MS = [2000, 4000, 8000] as const;

async function fetchLocalFallbackTiersWithRetry(): Promise<{ tier: TierInfo; nextTier: NextTierInfo | null; previousTier: PreviousTierInfo | null }> {
  for (let attempt = 0; attempt <= LOCAL_RETRY_DELAYS_MS.length; attempt++) {
    try {
      const tiers: TierDefinition[] = await send('get', 'realtime/admin/rewards/tiers');
      return parseTierDefinitions(tiers);
    } catch {
      if (attempt < LOCAL_RETRY_DELAYS_MS.length) {
        const delay = LOCAL_RETRY_DELAYS_MS[attempt];
        console.debug(`[useTierEvaluation] Local tier fetch attempt ${attempt + 1} failed, retrying in ${delay}ms…`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  throw new Error('Local tier definition fetch failed after all retries');
}

function applyTierDetail(
  detail: any,
  setTier: (t: TierInfo) => void,
  setNextTier: (t: NextTierInfo | null) => void,
  setPreviousTier: (t: PreviousTierInfo | null) => void,
): void {
  setTier({
    tierId: detail.assignedTierId,
    name: detail.assignedTierName,
    canEarn: detail.canEarn,
    priority: detail.priority,
    earnRateDescription: detail.earnRateDescription,
    maxEarningsDescription: detail.maxEarningsDescription,
    detailSummary: detail.detailSummary,
    componentChecks: Array.isArray(detail.componentChecks) ? detail.componentChecks : [],
  });
  setNextTier(detail.nextTier ? {
    tierId: detail.nextTier.tierId,
    name: detail.nextTier.name,
    earnRateDescription: detail.nextTier.earnRateDescription,
    maxEarningsDescription: detail.nextTier.maxEarningsDescription,
    componentChecks: Array.isArray(detail.nextTier.componentChecks)
      ? detail.nextTier.componentChecks
      : [],
  } : null);
  setPreviousTier(detail.previousTier ? {
    tierId: detail.previousTier.tierId,
    name: detail.previousTier.name,
    earnRateDescription: detail.previousTier.earnRateDescription,
    maxEarningsDescription: detail.previousTier.maxEarningsDescription,
  } : null);
}

/**
 * Fetches tier data from the Machine record, mirroring how the admin-dashboard
 * fetches machine details via fetchMachineDetail(machineId).
 *
 * The tier detail is pre-computed by TierEvaluationService.evaluate() whenever
 * the gaming score is computed, and persisted as `machineTierDetail` on the
 * Machine document. This hook simply reads that field.
 *
 * If the field is not yet populated (fresh install — score hasn't been computed
 * yet), the hook subscribes to GamingScoreCoordinator's `authoritativeScoreReceived`
 * event and re-fetches once the score arrives, keeping `loading = true` until
 * real data is available.
 *
 * On local dev (no PlayJector), falls back to fetching tier definitions
 * directly so the Welcome / About Tiers pages are testable.
 */
export function useTierEvaluation(machineId: string | null): UseTierEvaluationResult {
  const [tier, setTier] = useState<TierInfo | null>(null);
  const [nextTier, setNextTier] = useState<NextTierInfo | null>(null);
  const [previousTier, setPreviousTier] = useState<PreviousTierInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [initialFetchFailed, setInitialFetchFailed] = useState(false);
  const unsubRef = useRef<(() => void) | null>(null);
  const rawMetricsUnsubRef = useRef<(() => void) | null>(null);

  const retry = useCallback(() => {
    console.debug('[useTierEvaluation] Manual retry triggered');
    setLoading(false);
    setRetryCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanupListener = () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      if (rawMetricsUnsubRef.current) {
        rawMetricsUnsubRef.current();
        rawMetricsUnsubRef.current = null;
      }
    };

    const cleanupTimeout = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const fetchTierDetail = async (): Promise<'found' | 'not-found' | 'error'> => {
      try {
        const data = await send('get', `realtime/rewards/machine-tier/${encodeURIComponent(machineId!)}`);
        if (cancelled) return 'error';

        const detail = data?.tierDetail;
        if (detail) {
          applyTierDetail(detail, setTier, setNextTier, setPreviousTier);
          setError(null);
          return 'found';
        }
        return 'not-found';
      } catch {
        return 'error';
      }
    };

    const handleTimeout = async () => {
      timeoutId = null;
      if (cancelled || resolved) return;
      resolved = true;

      const timeoutErr = new GamingScoreTimeoutError(machineId!, {
        hasAuthoritative: gamingScoreCoordinator.hasAuthoritative(),
        rawMetricsReceived: gamingScoreCoordinator.getRawMetricsTimestamp() !== null,
        elapsedMs: TIER_TIMEOUT_MS,
      });
      if (!shouldThrottleGameScoreError(timeoutErr.name, machineId!)) {
        Sentry.captureException(timeoutErr, { extra: timeoutErr.context });
      }

      try {
        const data = await send(
          'post',
          `realtime/rewards/machine-tier/${encodeURIComponent(machineId!)}/fallback`,
        );
        if (cancelled) return;

        const detail = data?.tierDetail;
        if (detail) {
          applyTierDetail(detail, setTier, setNextTier, setPreviousTier);
          setError(null);
        } else {
          setError('Fallback did not return tier data');
        }
      } catch {
        if (cancelled) return;
        setError('Failed to load tier data');
      }

      cleanupListener();
      if (!cancelled) setLoading(false);
    };

    const run = async () => {
      if (!machineId) return;

      if (!cancelled) {
        setLoading(true);
        setInitialFetchDone(false);
        setInitialFetchFailed(false);
      }

      const fetchResult = await fetchTierDetail();
      if (cancelled) return;
      setInitialFetchDone(true);
      if (fetchResult === 'error') setInitialFetchFailed(true);

      if (fetchResult === 'found') {
        resolved = true;
        cleanupListener();
        setLoading(false);
        return;
      }

      if (constants.BRANCH === 'local' && !LOCAL_SKIP_FALLBACK) {
        console.debug('[useTierEvaluation] No machineTierDetail — using local tier definition fallback');
        try {
          const fallback = await fetchLocalFallbackTiersWithRetry();
          if (cancelled) return;
          resolved = true;
          setTier(fallback.tier);
          setNextTier(fallback.nextTier);
          setPreviousTier(fallback.previousTier);
          setError(null);
          setLoading(false);
          return;
        } catch {
          if (cancelled) return;
          resolved = true;
          console.debug('[useTierEvaluation] Local fallback failed after retries');
          setError('Failed to load tier data');
          setLoading(false);
          return;
        }
      }

      console.debug('[useTierEvaluation] machineTierDetail not yet available — waiting for score computation');
      cleanupListener();
      let minimumCalculatedAt = gamingScoreCoordinator.getRawMetricsMachineTimestamp();
      rawMetricsUnsubRef.current = gamingScoreCoordinator.on('rawMetricsReceived', (event) => {
        minimumCalculatedAt =
          minimumCalculatedAt === null
            ? event.machineIdTimestamp
            : Math.max(minimumCalculatedAt, event.machineIdTimestamp);
      });
      unsubRef.current = gamingScoreCoordinator.on('authoritativeScoreReceived', async ({ score }) => {
        if (cancelled || resolved) return;
        if (minimumCalculatedAt !== null && score.calculatedAt < minimumCalculatedAt) {
          console.debug('[useTierEvaluation] Ignoring stale score while fresh metrics are pending', {
            scoreCalculatedAt: score.calculatedAt,
            minimumCalculatedAt,
          });
          return;
        }
        console.debug('[useTierEvaluation] Score received — re-fetching tier detail');
        const result = await fetchTierDetail();
        if (cancelled || resolved) return;
        if (result === 'found') {
          resolved = true;
          cleanupListener();
          cleanupTimeout();
          setLoading(false);
        }
      });

      timeoutId = setTimeout(handleTimeout, TIER_TIMEOUT_MS);
    };

    run();
    return () => {
      cancelled = true;
      cleanupListener();
      cleanupTimeout();
    };
  }, [machineId, retryCount]);

  return { tier, nextTier, previousTier, loading, error, retry, initialFetchDone, initialFetchFailed };
}
